import { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import { plansService } from "services";

/**
 * Hook para obtener los turnos planificados (con time_intervals) de cada
 * empleado/día visible en la tabla de Attendance Supervisor.
 *
 * Emula el patrón de `useObjectivePlannedHours`:
 *  - Recibe los employee_ids descubiertos por `useAttendanceData` vía
 *    `fetchForEmployees`.
 *  - Mantiene un cache por employee_id y expone un índice
 *    `plannedShifts[employeeId_YYYY-MM-DD] = [{ shift, time_intervals }, ...]`
 *    listo para lookup O(1) desde las celdas de la tabla.
 *
 * La llamada base es `plansService.getPlanAssignments` (ruta
 * `/plans/assignments`). Este endpoint solo acepta `employee_id` singular, por
 * lo que se lanzan `N` requests en paralelo para los IDs nuevos.
 */
export default function usePlannedShifts(filterParams) {
	const [plannedShifts, setPlannedShifts] = useState({});

	const filterParamsRef = useRef(filterParams);
	filterParamsRef.current = filterParams;

	const loadedEmployeeIdsRef = useRef(new Set());
	const inflightAbortRef = useRef(null);

	useEffect(() => {
		if (inflightAbortRef.current) {
			inflightAbortRef.current.abort();
			inflightAbortRef.current = null;
		}
		setPlannedShifts({});
		loadedEmployeeIdsRef.current = new Set();
	}, [
		filterParams?.startDate,
		filterParams?.endDate,
		filterParams?.employeeIds,
		filterParams?.groupId,
	]);

	const doFetch = useCallback((ids) => {
		const fp = filterParamsRef.current;
		if (!fp?.startDate || !fp?.endDate) return;
		if (!ids || ids.length === 0) return;

		ids.forEach((id) => loadedEmployeeIdsRef.current.add(id));

		const abortController = new AbortController();
		inflightAbortRef.current = abortController;

		Promise.all(
			ids.map((id) =>
				plansService
					.getPlanAssignments({
						employee_id: id,
						start: fp.startDate,
						end: fp.endDate,
						signal: abortController.signal,
					})
					.catch((err) => {
						if (err?.name === "AbortError") return null;
						console.error(
							`Error fetching planned shifts for employee ${id}:`,
							err
						);
						return null;
					})
			)
		).then((responses) => {
			if (abortController.signal.aborted) return;

			const updates = {};

			responses.forEach((response, idx) => {
				const fallbackEmployeeId = ids[idx];
				if (!response || !response.ok || !Array.isArray(response.data)) return;

				response.data.forEach((entry) => {
					const empId = entry.employee?.id || fallbackEmployeeId;

					(entry.plans || []).forEach((plan) => {
						const dayKey = moment(plan.day).format("YYYY-MM-DD");
						const key = `${empId}_${dayKey}`;

						(plan.assignments || []).forEach((assignment) => {
							const intervals =
								(assignment.time_intervals &&
									assignment.time_intervals.length > 0
									? assignment.time_intervals
									: assignment.shift?.time_intervals) || [];

							if (!updates[key]) updates[key] = [];
							updates[key].push({
								shift: assignment.shift,
								time_intervals: intervals,
							});
						});
					});
				});
			});

			if (Object.keys(updates).length === 0) return;

			setPlannedShifts((prev) => ({ ...prev, ...updates }));
		});
	}, []);

	const fetchForEmployees = useCallback(
		(employeeIds) => {
			const newIds = employeeIds.filter(
				(id) => !loadedEmployeeIdsRef.current.has(id)
			);
			if (newIds.length === 0) return;
			doFetch(newIds);
		},
		[doFetch]
	);

	useEffect(() => {
		return () => {
			if (inflightAbortRef.current) {
				inflightAbortRef.current.abort();
			}
		};
	}, []);

	return {
		plannedShifts,
		fetchForEmployees,
	};
}
